import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { contactsApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Wand2, Search, Mail, Phone, Building2, Settings2 } from "lucide-react";
import ContactDialog from "@/components/ContactDialog";
import UniversalQuickAdd from "@/components/UniversalQuickAdd";
import { toast } from "sonner";

function draftToContactPreset(rec) {
  const f = rec.fields || {};
  return {
    full_name: f.full_name || "",
    email: f.email || "",
    phone: f.phone || "",
    company: f.company || "",
    role: f.role || "",
    preferred_channel: f.preferred_channel || "email",
    notes: f.notes || "",
  };
}

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, contact: null });
  const [quickOpen, setQuickOpen] = useState(false);

  const load = async () => {
    const r = await contactsApi.list({ q });
    setContacts(r.data);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]);

  const handleEditDetails = (rec) => {
    if (rec.type !== "contact") { toast.error("Advanced form only supports contacts from this section"); return; }
    setDlg({ open: true, contact: draftToContactPreset(rec) });
  };

  return (
    <div className="h-full overflow-y-auto p-8" data-testid="contacts-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-eyebrow mb-2">Central Directory</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Contacts</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input placeholder="Search contacts…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9 w-72 bg-white/5 border-white/10" data-testid="contacts-search"/>
          </div>
          <Button onClick={() => setQuickOpen(true)} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="new-contact-btn"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>
          <Button variant="ghost" onClick={() => setDlg({ open: true, contact: null })} className="text-white/70 hover:text-white hover:bg-white/10 text-xs" data-testid="new-contact-advanced-btn"><Settings2 className="w-3.5 h-3.5 mr-1.5"/>Advanced form</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="contacts-grid">
        {contacts.length === 0 && <div className="col-span-full text-center py-16 text-white/40 border border-dashed border-white/10 rounded-xl">No contacts.</div>}
        {contacts.map(c => (
          <div key={c.id} className="p-5 rounded-xl border border-white/10 bg-[#151A23] hover:border-white/25 transition-colors" data-testid={`contact-card-${c.id}`}>
            <div className="flex items-start justify-between mb-2">
              <Link to={`/contacts/${c.id}`} className="font-display font-semibold text-lg hover:underline">{c.full_name}</Link>
              <button onClick={() => setDlg({ open: true, contact: c })} className="text-xs text-white/50 hover:text-white">Edit</button>
            </div>
            {c.role && <div className="text-sm text-white/70">{c.role}</div>}
            {c.company && <div className="text-xs text-white/50 flex items-center gap-1.5 mt-1"><Building2 className="w-3 h-3"/>{c.company}</div>}
            <div className="mt-3 space-y-1">
              {c.email && <div className="text-xs text-white/60 flex items-center gap-1.5"><Mail className="w-3 h-3"/>{c.email}</div>}
              {c.phone && <div className="text-xs text-white/60 flex items-center gap-1.5"><Phone className="w-3 h-3"/>{c.phone}</div>}
            </div>
            {(c.entity_ids?.length || 0) > 0 && <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40">Linked to {c.entity_ids.length} entit{c.entity_ids.length === 1 ? "y" : "ies"}</div>}
          </div>
        ))}
      </div>

      <ContactDialog open={dlg.open} setOpen={(v) => setDlg({ ...dlg, open: v })} contact={dlg.contact} onSaved={() => { setDlg({ open: false, contact: null }); load(); }}/>
      <UniversalQuickAdd open={quickOpen} setOpen={setQuickOpen} section="contacts" onCreated={load} onEditDetails={handleEditDetails}/>
    </div>
  );
}
